import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { NotifierService } from 'angular-notifier';
import { faTrashAlt } from '@fortawesome/free-solid-svg-icons';
import { catchError, Subscription, switchMap, firstValueFrom } from 'rxjs';
import { BoobleModalService } from 'booble-modal';
import { AddressService } from '../../../../shared/services/address.service';
import { RegisterService } from '../../../../shared/services/register.service';
import { ContactModal } from 'src/app/shared/modals/contact/contact-modal.component';
import { CancelModal } from 'src/app/shared/modals/cancel/cancel-modal.component';
import { CompanyResponsibleDto } from 'src/app/domain/models/dto/company-responsible.dto';
import { CompanyPartnerService } from 'src/app/shared/services/company-partner.service';
import { AuthenticationService } from 'src/app/shared/services';
import { CompanyResponsibleService } from 'src/app/shared/services/company-responsible.service';
import { RegisterStepType } from 'src/app/domain/enums/register-step-types.enum';
import { CorporateAccountService } from 'src/app/shared/services/corporate-account.service';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';
import { AddressDto } from 'src/app/domain/models/dto/address.dto';
import { ForeignAccountDataDto } from 'src/app/domain/models/dto/foreign-account-data.dto';
import { FatcaComponent } from 'src/app/shared/modals/fatca/fatca.component';
import { CepResponse } from 'src/app/domain/models/interfaces/cep-response.interface';
 
@Component({
  selector: 'app-responsible',
  templateUrl: './responsible.component.html',
  styleUrls: ['./responsible.component.scss'],
})
export class ResponsibleComponent implements OnInit, OnDestroy {
  private readonly notifier: NotifierService;
  faTrashAlt = faTrashAlt;
  tenant: string = '';
  public loading: boolean;
  showAddress = false;

  requestError: boolean;
  public loadingCep: boolean;
  private lastSearchedCeps = new Map<FormGroup, string>();
  forms: FormGroup[] = [];
  formSubscriptions: Subscription[] = [];
  data: CompanyResponsibleDto[] = [];

  get responsibles(): CompanyResponsibleDto[] {
    let responsibles = [];
    this.forms.forEach((form) => {
      let responsible = this.data.find((c) => c.id == form.getRawValue().id);
      responsible = form.getRawValue() as CompanyResponsibleDto;
      responsible.personalCheck = this.data.find(
        (c) => c.id == form.getRawValue().id
      ).personalCheck;
      responsible.address = this.data.find(
        (c) => c.id == form.getRawValue().id
      ).address;
      if (responsible.hasCompanyControl) {
        responsible.address ??= new AddressDto();
        responsible.address.id = form.getRawValue().addressId;
        responsible.address.line1 = form.getRawValue().line1;
        responsible.address.line2 = form.getRawValue().line2;
        responsible.address.line3 = form.getRawValue().line3;
        responsible.address.city = form.getRawValue().city;
        responsible.address.state = form.getRawValue().state;
        responsible.address.number = form.getRawValue().number;
        responsible.address.postalCode = form.getRawValue().postalCode;
      }
      if (form.getRawValue().isFATCA) {
        responsible.foreignAccountData ??= new ForeignAccountDataDto();
        responsible.foreignAccountData.bornAmerican =
          form.getRawValue().bornAmerican;
        responsible.foreignAccountData.naturalizedAmerican =
          form.getRawValue().naturalizedAmerican;
        responsible.foreignAccountData.hasGreenCard =
          form.getRawValue().hasGreenCard;
        responsible.foreignAccountData.partnerInAmericanCompany =
          form.getRawValue().partnerInAmericanCompany;
        responsible.foreignAccountData.taxResidenceEUA =
          form.getRawValue().taxResidenceEUA;
        responsible.foreignAccountData.identificationNumber =
          form.getRawValue().identificationNumber;
      }

      delete (responsible as any).validationResult;
      responsibles.push(responsible);
    });
    return responsibles;
  }

  socioType = 'PF';
  documentType = 'CPF';
  constructor(
    notifierService: NotifierService,
    private readonly formBuilder: FormBuilder,
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly modalService: BoobleModalService,
    private readonly addressService: AddressService,
    private readonly registerService: RegisterService,
    private readonly companyResponsibleService: CompanyResponsibleService,
    private readonly corporateAccountService: CorporateAccountService
  ) {
    this.notifier = notifierService;
  }

  ngOnDestroy(): void {
    if (this.formSubscriptions?.length > 0)
      this.formSubscriptions.forEach((subs) => subs.unsubscribe());
  }

  ngOnInit(): void {
    this.route.paramMap.subscribe((params) => {
      const result = params.get('tenant');

      if (result) this.tenant = params.get('tenant');
    });

    this.companyResponsibleService.getByCompany().subscribe({
      next: (corporateAccountResult) => {
        this.data = corporateAccountResult.data;
        const singleResponsible = this.data?.length === 1;
        corporateAccountResult.data.forEach((responsible) => {
          const form = this.formBuilder.group({
            id: [responsible.id],
            firstName: [responsible.firstName, Validators.pattern('.*\\S.*')],
            document: [responsible.document, Validators.pattern('.*\\S.*')],
            phoneNumber: [
              responsible.phoneNumber,
              Validators.pattern('.*\\S.*'),
            ],
            email: [
              responsible.email,
              [Validators.pattern('.*\\S.*'), Validators.email],
            ],
            participation: [
              responsible.participation,
              Validators.pattern('.*\\S.*'),
            ],
            hasCompanyControl: [responsible?.hasCompanyControl],
            identificationNumber: [
              responsible?.foreignAccountData?.identificationNumber,
            ],
            isFATCA: [responsible?.isFATCA],
            bornAmerican: [responsible?.foreignAccountData?.bornAmerican],
            naturalizedAmerican: [
              responsible?.foreignAccountData?.naturalizedAmerican,
            ],
            hasGreenCard: [responsible?.foreignAccountData?.hasGreenCard],
            taxResidenceEUA: [responsible?.foreignAccountData?.taxResidenceEUA],
            partnerInAmericanCompany: [
              responsible?.foreignAccountData?.partnerInAmericanCompany,
            ],
            addressId: [responsible.address?.id],
          });
          form.controls['firstName'].disable();
          form.controls['document'].disable();

          if (responsible?.foreignAccountData) {
            form.controls['isFATCA'].setValue(true);
          }

          if (singleResponsible) {
            form.controls['participation'].setValue(100);
            form.controls['hasCompanyControl'].setValue(true, {
              emitEvent: false,
            });
          }

          this.formAddressCheck(responsible, form);

          this.formSubscriptions.push(
            form.valueChanges.subscribe({
              next: (value: CompanyResponsibleDto) => {
                /*   if (form.value.participation >= 25) {
                  form.controls['hasCompanyControl'].setValue(true, {
                    emitEvent: false,
                  });
                } */
                this.formAddressCheck(form.value, form);
              },
            })
          );

          form.updateValueAndValidity();
          // form.controls['isFATCA']
          //   .valueChanges
          //   .subscribe({
          //     next: async (value: boolean) => {
          //       if (!value) {

          //       }
          //     }
          //   })

          this.forms.push(form);
        });
      },
      error: (err) => {
        console.error(err);
      },
    });
  }

  private formAddressCheck(value: CompanyResponsibleDto, form: FormGroup) {
    if (value.hasCompanyControl) {
      this.addAddressToForm(form, value);
    } else {
      this.removeAddressFromForm(form);
    }
  }

  setAddress(form: FormGroup) {
    if (this.loadingCep) return;
    const postalCode: string = form.getRawValue().postalCode?.replace(/\D/g, '') || '';
    if (postalCode.length === 8 && postalCode !== this.lastSearchedCeps.get(form)) {
      this.lastSearchedCeps.set(form, postalCode);
      this.loadingCep = true;
      this.addressService.getCep(postalCode).subscribe((res: CepResponse) => {
        if (res.error) {
          form.controls['line1'].setValue('');
          form.controls['line3'].setValue('');
          form.controls['city'].setValue('');
          form.controls['state'].setValue('');
          this.notifier.notify(
            'warning',
            'Não foi possível buscar o CEP. Preencha o endereço manualmente.'
          );
        } else {
          if (res.street) {
            form.controls['line1'].setValue(res.street);
          }
          if (res.neighborhood) {
            form.controls['line3'].setValue(res.neighborhood);
          }
          if (res.city) {
            form.controls['city'].setValue(res.city);
          }
          if (res.state) {
            form.controls['state'].setValue(res.state);
          }
        }
        this.loadingCep = false;
      });
    }
  }

  private addAddressToForm(form: FormGroup, value: CompanyResponsibleDto) {
    if (!form.contains('line1'))
      form.addControl(
        'line1',
        new FormControl(value.address?.line1, [
          Validators.required,
          Validators.pattern('.*\\S.*'),
        ])
      );

    if (!form.contains('line2'))
      form.addControl('line2', new FormControl(value.address?.line2));

    if (!form.contains('line3'))
      form.addControl('line3', new FormControl(value.address?.line3));

    if (!form.contains('city'))
      form.addControl(
        'city',
        new FormControl(value.address?.city, [
          Validators.required,
          Validators.pattern('.*\\S.*'),
        ])
      );

    if (!form.contains('postalCode'))
      form.addControl(
        'postalCode',
        new FormControl(value.address?.postalCode, [
          Validators.required,
          Validators.pattern('.*\\S.*'),
          Validators.minLength(8),
          Validators.maxLength(9),
        ])
      );

    if (!form.contains('number'))
      form.addControl(
        'number',
        new FormControl(value.address?.number, [
          Validators.required,
          Validators.pattern('.*\\S.*'),
        ])
      );

    if (!form.contains('state'))
      form.addControl(
        'state',
        new FormControl(value.address?.state, [
          Validators.required,
          Validators.pattern('.*\\S.*'),
        ])
      );

    if (!form.contains('email'))
      form.addControl(
        'email',
        new FormControl(value?.email, [
          Validators.pattern('.*\\S.*'),
          Validators.email,
        ])
      );

    if (!form.contains('phoneNumber'))
      form.addControl(
        'phoneNumber',
        new FormControl(value?.phoneNumber, [Validators.pattern('.*\\S.*')])
      );
  }

  requiredSSN(form: FormGroup, value: boolean) {
    if (value && form.contains('identificationNumber')) {
      form.controls['identificationNumber'].setValidators([
        Validators.required,
        Validators.pattern('.*\\S.*'),
      ]);
    } else if (!value && form.contains('identificationNumber')) {
      form.controls['identificationNumber'].clearValidators();
      form.controls['identificationNumber'].setErrors(null);
      form.controls['identificationNumber'].updateValueAndValidity();
      form.controls['identificationNumber'].setValue('');
      form.controls['bornAmerican'].setValue(false);
      form.controls['naturalizedAmerican'].setValue(false);
      form.controls['hasGreenCard'].setValue(false);
      form.controls['taxResidenceEUA'].setValue(false);
      form.controls['partnerInAmericanCompany'].setValue(false);
    }
  }
  private removeAddressFromForm(form: FormGroup) {
    if (form.contains('line1')) form.removeControl('line1');
    if (form.contains('line2')) form.removeControl('line2');
    if (form.contains('line3')) form.removeControl('line3');
    if (form.contains('city')) form.removeControl('city');
    if (form.contains('postalCode')) form.removeControl('postalCode');
    if (form.contains('number')) form.removeControl('number');
    if (form.contains('state')) form.removeControl('state');
    if (form.contains('phoneNumber')) form.removeControl('phoneNumber');
    if (form.contains('email')) form.removeControl('email');
  }

  formsValid() {
    return this.forms.every((form) => form.valid);
  }

  ngAfterViewInit() {}

  openContactModal() {
    this.modalService.present(ContactModal);
  }

  openFatcaModal() {
    this.modalService.present(FatcaComponent);
  }

  getResponsibleCep(cep: string, index: number) {
    this.addressService.getCep(cep).subscribe((res: CepResponse) => {
      if (res.error) {
        this.notifier.notify('error', 'CEP inválido!');
      }
    });
  }

  async register() {
    const hasAtLeastOneWithControl = this.responsibles.some(
      (r) => r.hasCompanyControl
    );

    if (!hasAtLeastOneWithControl) {
      this.notifier.notify(
        'error',
        'Pelo menos um responsável deve possuir direito de assinar pela empresa.'
      );
      return;
    }

    this.loading = true;
    let members = {
      companyResponsibles: this.responsibles,
    };

    this.corporateAccountService
      .validateMembers(members)
      .pipe(
        switchMap((resultMembers) => {
          return this.corporateAccountService.updateMembers(resultMembers.data);
        }),
        catchError((err) => {
          this.loading = false;
          return Of(err);
        }),
        switchMap((resultCompany) => {
          return this.registerService.putRegisterStep(
            RegisterStepType.CompanyResponsibles
          );
        })
      )
      .subscribe({
        next: () => {
          const responsibles = Object.assign([], members.companyResponsibles);
          responsibles.forEach(async (r) => {
            if (!r.isFATCA) {
              await firstValueFrom(
                this.companyResponsibleService.deleteFatca(r.id)
              );
            }
          });
          this.goNext();
        },
      });
  }

  goNext() {
    this.router.navigate([`${this.tenant}/onboardings/bank-account`]);
  }

  goBack() {
    this.router.navigate([`${this.tenant}/onboardings/data`]);
  }
}

function Of(err): any {
  throw new Error(err);
}
